/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { put } from "@/shared/utils/fetchApi";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useTranslation } from "react-i18next";
import { finishInvitedOnboarding } from "../utils/finishInvitedOnboarding";
import type { OnboardingStep } from "../types/onboarding-types";

const SURVEY_STORAGE_KEY = "onboarding_survey";

function readSavedSurveyData(): Record<string, any> {
  if (typeof window === "undefined") return {};
  const raw = sessionStorage.getItem(SURVEY_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    // `typeof [] === "object"`: sem o Array.isArray, um array salvo por engano
    // passava como se fosse o formulário.
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    sessionStorage.removeItem(SURVEY_STORAGE_KEY);
    return {};
  }
}

export const useSurveySubmit = (
  setCurrentStep: (step: OnboardingStep) => void,
  surveyConfig: any,
  onStepCompleted?: () => void,
  // Convidado (já tem org): a survey é o último step do onboarding (bug 84095)
  finishAfterSurvey: boolean = false,
) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // Load saved survey data from sessionStorage
  //
  // Com guarda porque o valor vem do storage do navegador: um item corrompido
  // (ou de uma versão anterior do formulário) fazia o JSON.parse estourar na
  // renderização e derrubava o onboarding inteiro. Aqui o item inválido é
  // descartado e o formulário começa vazio.
  const savedSurveyData = readSavedSurveyData();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surveyData, setSurveyData] =
    useState<Record<string, any>>(savedSurveyData);
  const [surveyErrors, setSurveyErrors] = useState<Record<string, string>>({});

  // Campo "Função" movido do ProfileStep para cá
  const [functionValue, setFunctionValue] = useState<string>(
    savedSurveyData.user_function || "",
  );
  const [functionError, setFunctionError] = useState<string | null>(null);

  // Save survey data to sessionStorage on change (incluindo função)
  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(
        SURVEY_STORAGE_KEY,
        JSON.stringify({
          ...surveyData,
          user_function: functionValue,
        }),
      );
    }
  }, [surveyData, functionValue]);

  // Handler para mudança do campo função
  const handleFunctionChange = (value: string) => {
    setFunctionValue(value);
    if (functionError) {
      setFunctionError(null);
    }
  };

  // Função helper para pegar texto localizado
  const getLocalizedText = (text: any): string => {
    const currentLang = i18n.language.split("-")[0];
    if (typeof text === "string") return text;
    if (typeof text === "object" && text !== null) {
      return text[currentLang] || text["pt"] || text["en"] || "";
    }
    return "";
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setSurveyData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    // Limpar erro do campo quando usuário começar a preencher
    if (surveyErrors[fieldId]) {
      setSurveyErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSurveyErrors({});

    try {
      if (!user?.id) {
        throw new Error("User ID not found");
      }

      // Validar campo função (obrigatório, movido do ProfileStep)
      let hasFunctionError = false;
      if (!functionValue || functionValue.trim().length < 2) {
        setFunctionError(t("auth.onboarding.validation.functionMinLength"));
        hasFunctionError = true;
      } else if (functionValue.trim().length > 100) {
        setFunctionError(t("auth.onboarding.validation.functionMaxLength"));
        hasFunctionError = true;
      }

      // Validar campos obrigatórios do survey dinâmico
      const requiredFields =
        surveyConfig?.fields.filter((f: any) => f.required) || [];
      const errors: Record<string, string> = {};

      for (const field of requiredFields) {
        const fieldValue = surveyData[field.id];
        const isEmpty =
          !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);

        if (isEmpty) {
          const fieldLabel = getLocalizedText(field.label);
          errors[field.id] = t("auth.onboarding.validation.requiredField", {
            field: fieldLabel,
          });
        }
      }

      // Se houver erros, não prosseguir
      if (hasFunctionError || Object.keys(errors).length > 0) {
        setSurveyErrors(errors);
        setIsSubmitting(false);

        // Scroll para o primeiro campo com erro
        setTimeout(() => {
          const firstErrorField = document.querySelector(
            '[data-field-error="true"]',
          );
          if (firstErrorField) {
            firstErrorField.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);

        return;
      }

      // Buscar infos atuais do perfil
      const currentInfos = user?.user_metadata?.infos || {};

      // Atualizar profile com função e infos do survey
      const profilePayload = {
        position: functionValue.trim(), // Campo função salvo como position no profile
        infos: {
          ...currentInfos,
          onboarding_survey: {
            ...surveyData,
            completed_at: new Date().toISOString(),
            schema_version: surveyConfig?.schema_version || "1.0",
          },
        },
      };

      await put("/users/me", profilePayload);

      // Survey salvo com sucesso

      if (onStepCompleted) {
        onStepCompleted();
      }

      // Convidado conclui aqui; os demais avançam para a criação de organização
      if (finishAfterSurvey) {
        await finishInvitedOnboarding();
        return;
      }
      setCurrentStep("organization");
    } catch (err: any) {
      setError(err.message || t("auth.onboarding.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    onSubmit,
    isSubmitting,
    error,
    setError,
    surveyData,
    surveyErrors,
    handleFieldChange,
    // Campo função movido do ProfileStep
    functionValue,
    functionError,
    handleFunctionChange,
  };
};
